/**
 * Técnico responsável do ticket (pedido da PoolTrack, 02/10): seletor com busca
 * por nome, cidade e região. A lista vem do cadastro de técnicos — que a
 * sincronização com a origem alimenta, ~1.800 nomes —, então a busca é do
 * SERVIDOR e a tela mostra poucos de cada vez. Ativos por padrão; "Mostrar
 * inativos" inclui os desativados (corrigir depois do atendimento é legítimo).
 *
 * Usado nas duas visões: "Informações do ticket" (completa e o painel Detalhes
 * da compacta) e o cabeçalho da compacta (`compact`). A UX é a do SearchPicker,
 * a mesma do seletor de responsável (pedido da PoolTrack, 05/10).
 */
import { useQuery } from '@tanstack/react-query';
import { HardHat } from 'lucide-react';
import type { Technician } from '@erp/shared';
import {
  SearchPicker,
  type SearchPickerLabels,
  type UseSearchPickerResults,
} from '@/shared/SearchPicker';
import { TECHNICIAN_PICKER_LIMIT, technicianSearchQuery } from './service-orders-queries';

export interface TechnicianRefValue {
  id: number;
  name: string;
}

/** "Poço Fundo · Sul de Minas" — o que distingue dois homônimos na lista */
function technicianPlace(t: Pick<Technician, 'cities' | 'region'>): string {
  return [t.cities.join(', '), t.region].filter((p) => p && p.trim() !== '').join(' · ');
}

const useTechnicianResults: UseSearchPickerResults = ({ search, includeInactive, enabled }) => {
  const q = useQuery({ ...technicianSearchQuery(search, includeInactive), enabled });
  const list = q.data ?? [];
  return {
    items: list.map((t) => ({
      id: t.id,
      name: t.name,
      active: t.active,
      secondary: technicianPlace(t),
    })),
    // o servidor corta no limite: a lista cheia é sinal de que havia mais
    hasMore: list.length >= TECHNICIAN_PICKER_LIMIT,
    isPending: q.isPending,
    isError: q.isError,
  };
};

const LABELS: SearchPickerLabels = {
  aria: 'Técnico responsável',
  none: 'Sem técnico',
  clear: 'Sem técnico (limpar)',
  placeholder: 'Buscar por nome, cidade ou região…',
  notFound: 'Nenhum técnico encontrado',
  empty: 'Nenhum técnico cadastrado',
  error: 'Erro ao buscar técnicos',
};

export function TechnicianPicker({
  value,
  onChange,
  disabled,
  compact = false,
}: {
  value: TechnicianRefValue | null;
  /** o segundo argumento traz o nome, para quem guarda só o id (o diálogo da OS) */
  onChange: (technicianId: number | null, technician: TechnicianRefValue | null) => void;
  disabled?: boolean;
  /** cabeçalho da visão compacta: gatilho baixo e estreito */
  compact?: boolean;
}) {
  return (
    <SearchPicker
      value={value}
      onChange={(id, item) => onChange(id, item ? { id: item.id, name: item.name } : null)}
      useResults={useTechnicianResults}
      labels={LABELS}
      icon={HardHat}
      limit={TECHNICIAN_PICKER_LIMIT}
      disabled={disabled}
      compact={compact}
    />
  );
}
